/**
 * ActivityEventBadge
 *
 * A small circular badge rendered in the bottom-right corner of the activity
 * feed avatar. Each event type maps to a specific icon and background colour
 * according to the design spec:
 *
 *  blue   + Plus        → create task / project
 *  blue   + UserPlus    → add member / assign task
 *  red    + UserMinus   → remove member / unassign task
 *  orange + ImagePlus   → upload file
 *  green  + Check       → task completed
 *  green  + MapPin      → check-in (location matched)
 *  red    + MapPin      → check-in (location mismatch)
 *  secondary + Edit2/RotateCcw → update / restore
 *  grey04 + Folder → project archived
 *  secondary + RotateCcw → project archive restored
 *  red    + Trash2      → task deleted
 */
import React from 'react';
import { StyleSheet, View } from 'react-native';

import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

// ── Event → badge config map ──────────────────────────────────────────────

type BadgeConfig = {
  iconName: string;
  backgroundColor: string;
};

const EVENT_BADGE_MAP: Record<string, BadgeConfig> = {
  // ── Create ───────────────────────────────────────────────────────────────
  ProjectCreated: { iconName: 'Plus', backgroundColor: theme.colors.blue },
  TaskCreated: { iconName: 'Plus', backgroundColor: theme.colors.blue },

  // ── Assign / add member ──────────────────────────────────────────────────
  ProjectAssigned: { iconName: 'UserPlus', backgroundColor: theme.colors.blue },
  TaskAssigned: { iconName: 'UserPlus', backgroundColor: theme.colors.blue },

  // ── Remove member / unassign ─────────────────────────────────────────────
  ProjectUnassigned: {
    iconName: 'UserMinus',
    backgroundColor: theme.colors.alertRed,
  },
  ProjectUnassignedSelf: {
    iconName: 'UserMinus',
    backgroundColor: theme.colors.alertRed,
  },
  TaskUnassigned: {
    iconName: 'UserMinus',
    backgroundColor: theme.colors.alertRed,
  },
  TaskUnassignedSelf: {
    iconName: 'UserMinus',
    backgroundColor: theme.colors.alertRed,
  },

  // ── Upload file ──────────────────────────────────────────────────────────
  ProjectDocument: {
    iconName: 'ImagePlus',
    backgroundColor: theme.colors.orange,
  },
  TaskDocument: { iconName: 'ImagePlus', backgroundColor: theme.colors.orange },

  // ── Task completion / restore ────────────────────────────────────────────
  TaskCompleted: {
    iconName: 'Check',
    backgroundColor: theme.colors.greenSecondary,
  },
  TaskCompletedRestore: {
    iconName: 'RotateCcw',
    backgroundColor: theme.colors.textSecondary,
  },

  // ── Update ───────────────────────────────────────────────────────────────
  ProjectUpdated: {
    iconName: 'Edit2',
    backgroundColor: theme.colors.textSecondary,
  },
  ProjectReassigned: {
    iconName: 'Edit2',
    backgroundColor: theme.colors.textSecondary,
  },
  ProjectArchived: {
    iconName: 'Folder',
    backgroundColor: theme.colors.grey04,
  },
  ProjectArchiveRestored: {
    iconName: 'RotateCcw',
    backgroundColor: theme.colors.textSecondary,
  },
  TaskReassigned: {
    iconName: 'Edit2',
    backgroundColor: theme.colors.textSecondary,
  },

  // ── Check-in (location matched) ──────────────────────────────────────────
  // MessageSent with checkin=true — caller passes 'CheckinSuccess'
  CheckinSuccess: {
    iconName: 'MapPin',
    backgroundColor: theme.colors.greenSecondary,
  },

  // ── Check-in (location mismatch) ─────────────────────────────────────────
  // caller passes 'CheckinMismatch'
  CheckinMismatch: {
    iconName: 'MapPin',
    backgroundColor: theme.colors.alertRed,
  },

  // ── Message / generic ────────────────────────────────────────────────────
  MessageSent: {
    iconName: 'MessageCircle',
    backgroundColor: theme.colors.blue,
  },
  // MessageSent with a document attachment — caller passes 'MessageSentWithDocument'
  MessageSentWithDocument: {
    iconName: 'ImagePlus',
    backgroundColor: theme.colors.orange,
  },

  // ── Delete ───────────────────────────────────────────────────────────────
  TaskDeleted: { iconName: 'Trash2', backgroundColor: theme.colors.alertRed },
};

// ── Component ─────────────────────────────────────────────────────────────

interface ActivityEventBadgeProps {
  event: string;
}

const BADGE_SIZE = 16;

const ActivityEventBadge: React.FC<ActivityEventBadgeProps> = ({ event }) => {
  const config = EVENT_BADGE_MAP[event];
  if (!config) return null;

  return (
    <View
      style={[styles.badge, { backgroundColor: config.backgroundColor }]}
      accessibilityLabel={`Activity type: ${event}`}>
      <Icon
        name={config.iconName as any}
        color='white'
        width={BADGE_SIZE - 6}
        height={BADGE_SIZE - 6}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  badge: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: BADGE_SIZE,
    height: BADGE_SIZE,
    borderRadius: BADGE_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ActivityEventBadge;
